import React, { useState, ChangeEvent } from 'react';
import {
    Box,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Grid,
    Paper,
    SelectChangeEvent
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// Type definitions
interface PredefinedOffset {
    label: string;
    value: number;
}

interface QuickPreset {
    label: string;
    value: number;
    tooltip: string;
}

interface ReminderOffsetSelectorProps {
    value: number;
    onChange: (value: number) => void;
    label?: string;
    viewOnly: boolean;
}

interface CustomTimeUnit {
    value: string;
    unit: 'minutes' | 'hours' | 'days';
    direction: 'before' | 'after';
}

// Predefined offset options
const PREDEFINED_OFFSETS: PredefinedOffset[] = [
    { label: '5 minutes before', value: -5 },
    { label: '10 minutes before', value: -10 },
    { label: '15 minutes before', value: -15 },
    { label: '30 minutes before', value: -30 },
    { label: '1 hour before', value: -60 },
    { label: '2 hours before', value: -120 },
    { label: '1 day before', value: -1440 },
    { label: 'At event time', value: 0 },
    { label: '5 minutes after', value: 5 },
    { label: '15 minutes after', value: 15 },
    { label: '30 minutes after', value: 30 },
    { label: '1 hour after', value: 60 },
    { label: '2 hours after', value: 120 },
    { label: '1 day after', value: 1440 },
];

// Quick presets for common intervals
const QUICK_PRESETS: QuickPreset[] = [
    { label: '5m', value: -5, tooltip: '5 minutes before' },
    { label: '15m', value: -15, tooltip: '15 minutes before' },
    { label: '30m', value: -30, tooltip: '30 minutes before' },
    { label: '1h', value: -60, tooltip: '1 hour before' },
    { label: '1d', value: -1440, tooltip: '1 day before' },
    { label: 'At time', value: 0, tooltip: 'At event time' },
];

type ModeType = 'preset' | 'custom';
type TimeUnitType = 'minutes' | 'hours' | 'days';
type DirectionType = 'before' | 'after';

const ReminderOffsetSelector: React.FC<ReminderOffsetSelectorProps> = ({
    value,
    onChange,
    label = "Reminder",
    viewOnly
}) => {
    const [mode, setMode] = useState<ModeType>('preset');
    const [customValue, setCustomValue] = useState<string>(Math.abs(value).toString());
    const [customUnit, setCustomUnit] = useState<TimeUnitType>('minutes');
    const [customDirection, setCustomDirection] = useState<DirectionType>(value < 0 ? 'before' : 'after');

    // Handle preset selection
    const handlePresetChange = (newValue: number): void => {
        onChange(newValue);
    };

    // Handle mode change
    const handleModeChange = (
        _event: React.MouseEvent<HTMLElement>,
        newMode: ModeType | null
    ): void => {
        if (newMode !== null) {
            setMode(newMode);

            // When switching to custom mode, initialize with current value
            if (newMode === 'custom') {
                const absValue = Math.abs(value);
                if (absValue >= 1440 && absValue % 1440 === 0) {
                    setCustomValue((absValue / 1440).toString());
                    setCustomUnit('days');
                } else if (absValue >= 60 && absValue % 60 === 0) {
                    setCustomValue((absValue / 60).toString());
                    setCustomUnit('hours');
                } else {
                    setCustomValue(absValue.toString());
                    setCustomUnit('minutes');
                }
                setCustomDirection(value < 0 ? 'before' : 'after');
            }
        }
    };

    // Handle custom input changes
    const handleCustomChange = (): void => {
        const numValue = parseInt(customValue) || 0;
        let minutes = numValue;

        // Convert to minutes based on unit
        switch (customUnit) {
            case 'hours':
                minutes = numValue * 60;
                break;
            case 'days':
                minutes = numValue * 1440;
                break;
            default:
                minutes = numValue;
        }

        // Apply direction
        const finalValue = customDirection === 'before' ? -minutes : minutes;
        onChange(finalValue);
    };

    // Handle custom value change
    const handleCustomValueChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const newValue = e.target.value;
        setCustomValue(newValue);

        // Only update if it's a valid number
        const numValue = parseInt(newValue);
        if (!isNaN(numValue)) {
            const minutes = customUnit === 'hours' ? numValue * 60 :
                customUnit === 'days' ? numValue * 1440 :
                    numValue;
            const finalValue = customDirection === 'before' ? -minutes : minutes;
            onChange(finalValue);
        }
    };

    // Handle unit change
    const handleUnitChange = (e: SelectChangeEvent<TimeUnitType>): void => {
        const newUnit = e.target.value as TimeUnitType;
        setCustomUnit(newUnit);

        // Convert current value to new unit
        const numValue = parseInt(customValue) || 0;
        let convertedValue = numValue;
        let newMinutes = 0;

        if (newUnit === 'minutes') {
            if (customUnit === 'hours') convertedValue = numValue * 60;
            if (customUnit === 'days') convertedValue = numValue * 1440;
        } else if (newUnit === 'hours') {
            if (customUnit === 'minutes') convertedValue = Math.round(numValue / 60);
            if (customUnit === 'days') convertedValue = numValue * 24;
        } else if (newUnit === 'days') {
            if (customUnit === 'minutes') convertedValue = Math.round(numValue / 1440);
            if (customUnit === 'hours') convertedValue = Math.round(numValue / 24);
        }

        setCustomValue(convertedValue.toString());

        // Calculate new minutes
        switch (newUnit) {
            case 'hours':
                newMinutes = convertedValue * 60;
                break;
            case 'days':
                newMinutes = convertedValue * 1440;
                break;
            default:
                newMinutes = convertedValue;
        }

        const finalValue = customDirection === 'before' ? -newMinutes : newMinutes;
        onChange(finalValue);
    };

    // Handle direction change
    const handleDirectionChange = (
        _event: React.MouseEvent<HTMLElement>,
        newDirection: DirectionType | null
    ): void => {
        if (newDirection !== null) {
            setCustomDirection(newDirection);

            const numValue = parseInt(customValue) || 0;
            let minutes = numValue;

            switch (customUnit) {
                case 'hours':
                    minutes = numValue * 60;
                    break;
                case 'days':
                    minutes = numValue * 1440;
                    break;
            }

            const finalValue = newDirection === 'before' ? -minutes : minutes;
            onChange(finalValue);
        }
    };

    // Format minutes to readable string
    const formatMinutes = (minutes: number): string => {
        const absMinutes = Math.abs(minutes);

        if (absMinutes === 0) return 'At event time';

        if (absMinutes < 60) {
            return `${absMinutes} minute${absMinutes !== 1 ? 's' : ''} ${minutes < 0 ? 'before' : 'after'}`;
        } else if (absMinutes < 1440) {
            const hours = absMinutes / 60;
            return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes < 0 ? 'before' : 'after'}`;
        } else {
            const days = absMinutes / 1440;
            return `${days} day${days !== 1 ? 's' : ''} ${minutes < 0 ? 'before' : 'after'}`;
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" gutterBottom>
                {label}
            </Typography>

            <Box sx={{ mb: 2 }}>
                <ToggleButtonGroup
                    value={mode}
                    exclusive
                    onChange={handleModeChange}
                    size="small"
                    fullWidth
                    aria-label="selection mode"
                    disabled={viewOnly}
                >
                    <ToggleButton value="preset" aria-label="quick select">
                        Quick Select
                    </ToggleButton>
                    <ToggleButton value="custom" aria-label="custom">
                        Custom
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {mode === 'preset' ? (
                <>
                    {/* Full preset dropdown */}
                    <FormControl fullWidth size="small">
                        <InputLabel id="reminder-select-label">Select reminder time</InputLabel>
                        <Select
                            labelId="reminder-select-label"
                            value={value}
                            onChange={(e: SelectChangeEvent<number>) =>
                                handlePresetChange(Number(e.target.value))
                            }
                            label="Select reminder time"
                            startAdornment={<AccessTimeIcon sx={{ mr: 1, color: 'action.active' }} />}
                            disabled={viewOnly}
                        >
                            <MenuItem disabled value="">
                                <em>Select a reminder time</em>
                            </MenuItem>
                            {PREDEFINED_OFFSETS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </>
            ) : (
                <Grid container spacing={2} alignItems="center">
                        <Grid sx={{ xs: 12, sm:4 }} >
                        <ToggleButtonGroup
                            value={customDirection}
                            exclusive
                            onChange={handleDirectionChange}
                            size="small"
                            fullWidth
                                aria-label="reminder direction"
                                disabled={viewOnly}
                        >
                            <ToggleButton value="before" aria-label="before">
                                Before
                            </ToggleButton>
                            <ToggleButton value="after" aria-label="after">
                                After
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                        <Grid sx={{xs:6, sm:3 }}>
                        <TextField
                            type="number"
                            value={customValue}
                            onChange={handleCustomValueChange}
                            onBlur={handleCustomChange}
                            onKeyPress={(e: React.KeyboardEvent) =>
                                e.key === 'Enter' && handleCustomChange()
                            }
                            size="small"
                            fullWidth
                            inputProps={{
                                min: 0,
                                'aria-label': 'custom time value'
                                }}
                                disabled={viewOnly}
                        />
                    </Grid>

                        <Grid sx={{ xs:6, sm:3 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="time-unit-label">Unit</InputLabel>
                            <Select
                                labelId="time-unit-label"
                                value={customUnit}
                                onChange={handleUnitChange}
                                    label="Unit"
                                    disabled={viewOnly}
                            >
                                <MenuItem value="minutes">Minutes</MenuItem>
                                <MenuItem value="hours">Hours</MenuItem>
                                <MenuItem value="days">Days</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                        <Grid sx={{ xs: 12, sm: 2 }}>
                        <Typography variant="body2" color="text.secondary" align="center">
                            ({formatMinutes(value)})
                        </Typography>
                    </Grid>
                </Grid>
            )}

            {/* Current selection display */}
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: 'block' }}
            >
                Selected: {formatMinutes(value)}
            </Typography>
        </Box>
    );
};

// Alternative simpler version
interface SimpleReminderSelectorProps {
    value: number;
    onChange: (value: number) => void;
    label?: string;
    viewOnly: boolean;
}

const SimpleReminderSelector: React.FC<SimpleReminderSelectorProps> = ({
    value,
    onChange,
    label = "Reminder",
    viewOnly
}) => {
    const handleChange = (e: SelectChangeEvent<number>): void => {
        onChange(Number(e.target.value));
    };

    return (
        <FormControl fullWidth size="small">
            <InputLabel id="simple-reminder-label">{label}</InputLabel>
            <Select
                labelId="simple-reminder-label"
                value={value}
                onChange={handleChange}
                label={label}
                disabled={viewOnly}
            >
                <MenuItem value={0}>At event time</MenuItem>
                <MenuItem disabled>--- Before ---</MenuItem>
                <MenuItem value={-5}>5 minutes before</MenuItem>
                <MenuItem value={-10}>10 minutes before</MenuItem>
                <MenuItem value={-15}>15 minutes before</MenuItem>
                <MenuItem value={-30}>30 minutes before</MenuItem>
                <MenuItem value={-60}>1 hour before</MenuItem>
                <MenuItem value={-120}>2 hours before</MenuItem>
                <MenuItem value={-1440}>1 day before</MenuItem>
                <MenuItem disabled>--- After ---</MenuItem>
                <MenuItem value={5}>5 minutes after</MenuItem>
                <MenuItem value={15}>15 minutes after</MenuItem>
                <MenuItem value={30}>30 minutes after</MenuItem>
                <MenuItem value={60}>1 hour after</MenuItem>
            </Select>
        </FormControl>
    );
};

export default ReminderOffsetSelector;
export { SimpleReminderSelector };
export type { ReminderOffsetSelectorProps, SimpleReminderSelectorProps };